const binaryProtocol = require("@nemit/binary-protocol");

const recorder = require("./recorder");
const player = require("./player");
const log = require("./log");

const CLIENT_VERSION = process.env.COMMIT_HASH || "unknown";

log.log("Welcome to nemit, press connect to start. ");

const ping = (protocol) => {
	const start = Date.now();

	return protocol.call("echo", "echo").then(() => {
		const stop = Date.now();

		return stop - start;
	});
};

const estimateLatency = (protocol) => {
	const pings = Array(10).fill().reduce((prev) => {
		if(prev.length === 0) {
			prev.push(ping(protocol));

			return prev;
		}

		prev.push(prev.slice(-1)[0].then(() => ping(protocol)));

		return prev;
	}, []);

	return Promise.all(pings).then(pings => {
		return pings.reduce((prev, cur) => prev + cur * (1 / pings.length), 0);
	});
};

const openSocket = (onDisconnect) => {
	return new Promise((resolve, reject) => {
		const protocol = "ws" + window.location.protocol.slice(4, -1);

		let url = `${protocol}://${window.location.host}/nemit`;

		if(window.location.hostname === "localhost") {
			url = "wss://nemit.pro/nemit";
		}

		const webSocket = new WebSocket(url);
		webSocket.binaryType = "arraybuffer";

		let disconnected = false;
		const handleDisconnect = () => {
			if(disconnected) return;
			disconnected = true;
			onDisconnect();
		};

		webSocket.addEventListener("open", () => {
			resolve(webSocket);
		});

		webSocket.addEventListener("close", handleDisconnect);
		webSocket.addEventListener("error", handleDisconnect);
	});
};

const openNemit = (onDisconnect, playerInstances, onLatencyMeasured) => {
	return openSocket(onDisconnect).then(socket => {
		let lastBroadcastID = undefined;

		const protocol = binaryProtocol({
			broadcast: (data) => {
				if(lastBroadcastID && lastBroadcastID != data.id) {
					playerInstances[data.codec].reset();
				}

				// Convert samples to ArrayBuffer if needed (CLI sends Buffer, browser sends ArrayBuffer)
				let samples = data.samples;
				if(samples && !(samples instanceof ArrayBuffer)) {
					// Convert Uint8Array, Buffer, or array-like to ArrayBuffer
					samples = new Uint8Array(samples).buffer;
				}

				playerInstances[data.codec].decode(samples);

				lastBroadcastID = data.id;
			},

			getLock: (data) => {
				log.log("Someone is transmitting");
				button.classList.add("ptt-disabled");
				// Update buffer based on sender's latency + our own
				if(data && data.latency) {
					const senderLatency = data.latency;
					onLatencyMeasured(senderLatency, true); // true = from sender
				}
			},

			releaseLock: () => {
				// Wait for audio to finish playing before enabling button
				const waitForPlayback = () => {
					const stillPlaying = Object.values(playerInstances).some(p => p.isPlaying());
					if(stillPlaying) {
						setTimeout(waitForPlayback, 100);
					} else {
						button.classList.remove("ptt-disabled");
					}
				};
				waitForPlayback();
			},

			clientCount: (count) => {
				log.log(`${count} client${count !== 1 ? 's' : ''} connected`);
			},

			serverLog: (data) => {
				const level = data.level || "log";
				if(log[level]) {
					log[level](data.message);
				} else {
					log.log(data.message);
				}
			}
		}, (...args) => socket.send(...args));

		log.log("Estimating latency");

		estimateLatency(protocol).then(realLatency => {
			const latency = Math.round(realLatency);

			Object.values(playerInstances).forEach(el => el.setLatency(latency));
			onLatencyMeasured(latency);

			log.log(`Latency is around ${latency}ms. `);

			// Periodic latency check every 30s
			let lastLatency = latency;
			const latencyCheckInterval = setInterval(() => {
				ping(protocol).then(newLatency => {
					const rounded = Math.round(newLatency);
					if(Math.abs(rounded - lastLatency) > 50) {
						log.log(`Latency changed: ${lastLatency}ms → ${rounded}ms`);
					}
					lastLatency = rounded;
					onLatencyMeasured(rounded);
				}).catch(() => {
					// Connection probably lost, interval will be cleared on reconnect
					clearInterval(latencyCheckInterval);
				});
			}, 30000);

			// Store interval for cleanup on disconnect
			socket.addEventListener("close", () => clearInterval(latencyCheckInterval));
		});

		socket.onmessage = (message) => protocol.readMethod(message.data);

		return protocol;
	});
};

const button = document.querySelector("#ptt");

const connect = () => {
	button.removeEventListener("click", connect);

	// Check for mediaDevices support (requires HTTPS)
	if(!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
		log.error("Microphone not supported (requires HTTPS)");
		button.innerText = "Not supported";
		return;
	}

	button.innerText = "PTT";

	const ctxOption = {
		sampleRate: 24000
	};

	window.audioCtx = new (window.AudioContext || window.webkitAudioContext)(ctxOption)

	log.log(`Sample rate will be: ${window.audioCtx.sampleRate}`);

	// Play inaudible audio to keep iOS from suspending in background
	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
	if(isIOS) {
		const oscillator = window.audioCtx.createOscillator();
		const gain = window.audioCtx.createGain();
		oscillator.frequency.value = 1; // 1Hz - below human hearing
		gain.gain.value = 0.001; // Nearly silent
		oscillator.connect(gain);
		gain.connect(window.audioCtx.destination);
		oscillator.start();
	}

	// Request microphone permission early to avoid iOS issues with PTT
	navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
		stream.getTracks().forEach(track => track.stop());
		log.log("Microphone permission granted");
	}).catch(err => {
		log.error(`Microphone permission denied: ${err}`);
	});

	// Keep microphone permission alive on iOS (can expire over time)
	if(isIOS) {
		setInterval(() => {
			navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
				stream.getTracks().forEach(track => track.stop());
			}).catch(() => {});
		}, 30000);
	}

	let currentProtocol = null;
	let recorderInstance = false;
	let cancel;
	let tx;
	let txTimeout;
	let reconnectAttempts = 0;
	let reconnecting = false;
	let myLatency = 200; // Default, updated after ping

	const playerInstances = {
		"ogg/opus": player("ogg/opus"),
		"opus": player("opus")
	};

	const setupConnection = () => {
		if(reconnecting) return;
		reconnecting = true;

		const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
		if(reconnectAttempts > 0) {
			log.warning(`Reconnecting in ${delay / 1000}s...`);
		}

		setTimeout(() => {
			openNemit(() => {
				log.error("Disconnected from server");
				button.classList.add("ptt-disabled");
				currentProtocol = null;
				reconnecting = false;
				setupConnection();
			}, playerInstances, (latency, fromSender) => {
				if(fromSender) {
					// Sender's latency received - add to our own for total buffer
					const totalLatency = latency + myLatency;
					Object.values(playerInstances).forEach(el => el.setLatency(totalLatency));
					log.log(`Buffer: ${4 * totalLatency + 100}ms (4 × (${latency}ms sender + ${myLatency}ms mine) + 100ms jitter)`);
				} else {
					// Our own latency measured
					myLatency = latency;
				}
			}).then(protocol => {
				currentProtocol = protocol;
				const wasReconnect = reconnectAttempts > 0;
				reconnectAttempts = 0;
				reconnecting = false;

				if(wasReconnect) {
					log.log("Reconnected to server");
				} else {
					log.log("Connected to server");
				}

				protocol.call("echo", "connected").then(response => {
					console.log(response);
					button.classList.remove("ptt-disabled");
				});

				// Check version match
				protocol.call("version").then(serverVersion => {
					if(serverVersion !== CLIENT_VERSION) {
						log.warning(`Version mismatch: server ${serverVersion}, client ${CLIENT_VERSION} - refresh recommended`);
					}
				}).catch(() => {});
			}).catch(() => {
				reconnectAttempts++;
				reconnecting = false;
				setupConnection();
			});
		}, reconnectAttempts > 0 ? delay : 0);
	};

	const startTx = () => {
		if(!currentProtocol) return;

		cancel = false;
		tx = false;

		currentProtocol.call("getLock", { latency: myLatency }).then(lock => {
			tx = true;

			if(cancel) {
				return releaseHandler();
			}

			if(!lock) {
				log.error("Someone already talking");

				return;
			}

			log.log("You are transmitting");

			const rawModeEnabled = document.getElementById('raw-mode')?.checked || false;
			recorderInstance = recorder({ raw: rawModeEnabled });

			recorderInstance.on("samples", samples => {
				samples.arrayBuffer().then(buffer => {
					if(buffer.byteLength === 0) {
						return;
					}

					if(!currentProtocol) return;

					currentProtocol.call("broadcast", {
						codec: "ogg/opus",
						samples: buffer,
						id: lock
					});
				});
			});

			recorderInstance.record().then(() => {
				button.classList.add("ptt-pressed");
				button.innerText = "SAMO CQ";

				txTimeout = setTimeout(() => {
					console.warn("TX time limit reached (3 minutes), releasing lock");
					releaseHandler();
				}, 180000);
			});
		});
	};

	const releaseHandler = () => {
		clearTimeout(txTimeout);
		cancel = true;
		if(!tx) {
			return;
		}

		tx = false;

		log.log("You are not transmitting");

		button.classList.remove("ptt-pressed");
		button.innerText = "PTT";

		// Delay stop to capture last bit of audio
		setTimeout(() => {
			if(recorderInstance && recorderInstance.stop) {
				recorderInstance.stop();
			}
			if(currentProtocol) {
				currentProtocol.call("releaseLock");
			}
		}, 200);
	};

	const pressHandler = (e) => {
		button.setPointerCapture(e.pointerId);
		startTx();
	};

	button.addEventListener("pointerdown", pressHandler);
	button.addEventListener("pointerup", releaseHandler);
	button.addEventListener("pointercancel", releaseHandler);

	// Hardware PTT button support (Hytera, Zebra, Blackview, etc.)
	const pttKeyCodes = [102, 135, 261];
	document.addEventListener("keydown", (e) => {
		if(pttKeyCodes.includes(e.keyCode) && !e.repeat) {
			e.preventDefault();
			startTx();
		}
	});
	document.addEventListener("keyup", (e) => {
		if(pttKeyCodes.includes(e.keyCode)) {
			e.preventDefault();
			releaseHandler();
		}
	});

	button.addEventListener("touchstart", () => {
		if(window.audioCtx.state === 'suspended') {
			window.audioCtx.resume();
		}
	}, { passive: true });

	// Resume when returning from lock screen / background
	document.addEventListener("visibilitychange", () => {
		if(document.visibilityState === "visible") {
			if(window.audioCtx.state === 'suspended') {
				window.audioCtx.resume();
			}
			// Trigger reconnect if disconnected
			if(!currentProtocol && !reconnecting) {
				setupConnection();
			}
		}
	});

	setupConnection();
};

button.addEventListener("contextmenu", e => e.preventDefault());
button.addEventListener("click", connect);
